import { APIS, URLS } from '@/shared/config/urls';
import type { PageResponse } from '@/shared/types/abstract-api-response';
import { request } from '@/shared/utils/request';
import type { BookRequest, BookRequestComment, BookRequestFeed } from '../types/bookRequest.types';
import type { LikeState } from '../types/community.types';

const pageParams = (page: number, size: number) =>
  new URLSearchParams({ page: String(page), size: String(size) }).toString();

/** Timeline (every post) or Postingan Saya (the caller's), newest first. */
export const fetchBookRequests = async (
  feed: BookRequestFeed,
  page: number,
  size: number
): Promise<PageResponse<BookRequest>> => {
  const params = pageParams(page, size);
  const path = feed === 'mine' ? APIS.myBookRequests(params) : APIS.bookRequests(params);
  return await request.get(`${URLS.BACK_DSH}${path}`);
};

export const createBookRequest = async (content: string): Promise<BookRequest> => {
  return await request.post(`${URLS.BACK_DSH}${APIS.createBookRequest}`, { content });
};

/** Only the author's own post; 404 otherwise. */
export const deleteBookRequest = async (postId: string): Promise<void> => {
  await request.delete(`${URLS.BACK_DSH}${APIS.bookRequest(postId)}`);
};

/** Idempotent: liking twice keeps one like. */
export const setBookRequestLike = async (postId: string, liked: boolean): Promise<LikeState> => {
  const url = `${URLS.BACK_DSH}${APIS.bookRequestLike(postId)}`;
  return await (liked ? request.put(url) : request.delete(url));
};

/** Comments of a post, oldest first. */
export const fetchBookRequestComments = async (
  postId: string,
  page: number,
  size: number
): Promise<PageResponse<BookRequestComment>> => {
  return await request.get(
    `${URLS.BACK_DSH}${APIS.bookRequestComments(postId, pageParams(page, size))}`
  );
};

export const createBookRequestComment = async (
  postId: string,
  content: string
): Promise<BookRequestComment> => {
  return await request.post(`${URLS.BACK_DSH}${APIS.bookRequestComments(postId)}`, { content });
};

/** Only the author's own comment; 404 otherwise. */
export const deleteBookRequestComment = async (
  postId: string,
  commentId: string
): Promise<void> => {
  await request.delete(`${URLS.BACK_DSH}${APIS.bookRequestComment(postId, commentId)}`);
};

export const setBookRequestCommentLike = async (
  postId: string,
  commentId: string,
  liked: boolean
): Promise<LikeState> => {
  const url = `${URLS.BACK_DSH}${APIS.bookRequestCommentLike(postId, commentId)}`;
  return await (liked ? request.put(url) : request.delete(url));
};
